





import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import WorkspaceService from "services/workspace-service";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import { Typography } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MDTypography from "components/MDTypography";

import ProfileService from "services/profile-service";

import UpgradePopup from "layouts/components/Upgrade";
import SocialCard from "examples/Cards/SocialCard";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ProfileSearch from "../../../social-stash/components/toolbar/ProfileSearch";
import Grow from "@mui/material/Grow";

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 400,
    textAlign: "left",
  },
});

const tooltipContent = (
  <div style={{ maxWidth: "350px", textAlign: "left" }}>
    <Typography variant="body2" gutterBottom>
      <strong>How to Use Primary Selector:</strong>
    </Typography>
    <Typography variant="caption" display="block" gutterBottom>
      - View and manage saved <strong>own</strong> profiles. <br />
      - Select one own profile to compare with competitor profiles. <br />
      - Click “Continue” to proceed to select competitor profiles.
    </Typography>
    <Typography variant="body2" gutterBottom>
      <strong>Quick Actions:</strong>
    </Typography>
    <Typography variant="caption" display="block">
      - Use the search bar to filter profiles. <br />
      - Click “Add Profile” to link a new owned profile. <br />
    </Typography>
  </div>
);

const SelectPrimaryProfile = forwardRef(
  ({ selectedProfileId, onSelect, onAddProfileClick }, ref) => {
    const [ownProfiles, setOwnProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    // ✅ replaced responsive scroll-based top with dynamic AppBar+Stepper top
    const [toolbarTop, setToolbarTop] = useState(210);
    const [isTransparent, setIsTransparent] = useState(true);

    // [NEW] Usage Enforcement State
    const [usageRaw, setUsageRaw] = useState(null);
    const [showUpgrade, setShowUpgrade] = useState(false);

    useEffect(() => {
      const updateToolbarTop = () => {
        const navbar = document.querySelector("header.MuiAppBar-root");
        const stepper = document.querySelector(".MuiStepper-root");
        if (navbar && stepper) {
          // same logic as in MarketSnapshot
          const navHeight = navbar.offsetHeight;
          const stepperHeight = stepper.offsetHeight;
          const totalTop = navHeight + stepperHeight + 8;
          setToolbarTop(totalTop);
        } else if (navbar) {
          setToolbarTop(navbar.offsetHeight + 60);
        }
      };

      updateToolbarTop();

      const resizeObserver = new ResizeObserver(updateToolbarTop);
      resizeObserver.observe(document.body);

      const scrollHandler = () => setIsTransparent(window.scrollY === 0);
      window.addEventListener("resize", updateToolbarTop);
      window.addEventListener("scroll", scrollHandler);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", updateToolbarTop);
        window.removeEventListener("scroll", scrollHandler);
      };
    }, []);

    // [NEW] Fetch Usage Logic
    useEffect(() => {
      const fetchUsageData = async () => {
        try {
          const entitlements = await WorkspaceService.fetchWorkspaceEntitlements();
          if (!entitlements) return;

          setUsageRaw({
            profilesUsed: entitlements.usage?.profiles || 0,
            profilesLimit: entitlements.limits?.profiles || 0,
            currentPlan: entitlements.plan || "Basic"
          });
        } catch (error) {
          console.error("Error fetching usage details:", error);
        }
      };
      fetchUsageData();
    }, []);

    const fetchProfiles = async () => {
      try {
        setLoading(true);
        const workspaceProfiles = await WorkspaceService.fetchWorkspaceProfiles();
        const ownWorkspaceProfiles = workspaceProfiles.filter((p) => p.own === true);

        const groupByPlatform = ownWorkspaceProfiles.reduce((acc, p) => {
          if (!acc[p.platform]) acc[p.platform] = [];
          acc[p.platform].push(p.id);
          return acc;
        }, {});

        let fullProfiles = [];

        for (const platform of Object.keys(groupByPlatform)) {
          const ids = groupByPlatform[platform];
          if (ids.length > 0) {
            const profiles = await ProfileService.fetchProfile(platform, ids).catch(() => []);
            fullProfiles.push(...profiles.map((p) => ({ ...p, platform })));
          }
        }

        setOwnProfiles(fullProfiles);
      } catch (err) {
        console.error("Failed to load own profiles:", err);
        setOwnProfiles([]);
      } finally {
        setLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      refreshProfiles: fetchProfiles,
    }));

    useEffect(() => {
      fetchProfiles();
    }, []);

    const filteredProfiles = ownProfiles.filter((profile) =>
      (profile.name || "").toLowerCase().includes((searchQuery || "").toLowerCase())
    );

    const isSelected = (profileId) => selectedProfileId === profileId;

    // [NEW] Intercept Add Click
    const handleAddClick = () => {
      if (usageRaw) {
        const { profilesUsed, profilesLimit } = usageRaw;
        if (Number(profilesUsed) >= Number(profilesLimit)) {
          setShowUpgrade(true);
          return;
        }
      }
      if (onAddProfileClick) onAddProfileClick();
    };

    return (
      <MDBox>
        {/* ✅ Toolbar now fixed below Stepper; visual layout unchanged */}
        <MDBox
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{
            position: "sticky",
            top: `${toolbarTop}px`,
            zIndex: 999,
            backgroundColor: "#ffffff",
            padding: "5px",
            borderRadius: "8px",
            gap: 2,
            mb: 4,
            boxShadow: isTransparent ? "none" : "0 4px 12px rgba(0, 0, 0, 0.2)",
            transition: "top 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease",
          }}
        >

          {/* 🔍 Profile Search (Left Side) */}
          <MDBox flexGrow={1}>
            <ProfileSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </MDBox>
          {/* ➕ Connect Section (Right Side) */}
          <MDBox display="flex" flexDirection="column" alignItems="center">
            <MDTypography
              variant="button"
              fontWeight="medium"
              color="text"
              sx={{ marginBottom: 1, display: "block", textAlign: "center" }}
            >
              Connect
            </MDTypography>

            <MDBox
              bgColor="#f7f7f7"
              sx={{
                borderRadius: "16px",
                backgroundColor: "#f0f2f5",
                padding: "14px",
                display: "flex",
                flexDirection: "row", // ✅ place cards side by side
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {/* 🟢 Add Own Profile */}
              <Card
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "row",
                  gap: 1,
                  padding: "4px 18px",
                  borderRadius: "12px",
                  backgroundColor: "grey.100",
                  boxShadow: 1,
                  border: "1px solid #ddd",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                  "&:active": {
                    backgroundColor: "grey.400",
                    boxShadow: 6,
                  },
                }}
                onClick={handleAddClick}
              >
                <IconButton color="primary" sx={{ p: 0 }}>
                  <AddCircleIcon fontSize="large" />
                </IconButton>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="textPrimary"
                  ml={1}
                >
                  Add Own Profile
                </Typography>
              </Card>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Profile Card List */}
        <Card>
          <MDBox>
            <MDBox
              mx={2}
              mt={-3}
              py={1}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              flexDirection="row"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white">
                Own Profile
              </MDTypography>
              <CustomTooltip title={tooltipContent} placement="bottom" arrow>
                <IconButton sx={{ color: "white", p: 0, ml: 0.5 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </CustomTooltip>
            </MDBox>

            <MDBox px={2} py={3}>
              {loading ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress color="accent" />
                </MDBox>
              ) : filteredProfiles.length === 0 ? (
                <MDTypography variant="subtitle1" color="text" align="center">
                  No profile found.
                </MDTypography>
              ) : (
                <MDBox
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="center"
                  gap={2}
                  px={2}
                  mt={0}
                >
                  {filteredProfiles.map((profile, index) => (
                    <Grow
                      in={true}
                      timeout={index * 50}
                      key={profile.id}
                      style={{ transformOrigin: "0 0 0" }}
                    >
                      <MDBox
                        key={profile.id}
                        sx={{
                          flex: "1 1 calc(25% - 16px)",
                          minWidth: "240px",
                          maxWidth: "300px",
                        }}
                      >
                        <SocialCard
                          platform={profile.platform}
                          profilePic={profile.profilePictureUrl || ""}
                          name={profile.name || "Untitled"}
                          count={profile.count ?? 0}
                          link={profile.link}
                          verifiedStatus={profile.verifiedStatus ?? false}
                          isSelected={isSelected(profile.id)}
                          onSelect={() =>
                            isSelected(profile.id)
                              ? onSelect(null)
                              : onSelect(profile)
                          }
                        />
                      </MDBox>
                    </Grow>
                  ))}
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </Card>

        {/* [NEW] Upgrade Popup */}
        <UpgradePopup
          open={showUpgrade}
          onClose={() => setShowUpgrade(false)}
          type="usage"
          featureName="Tracked Profiles"
          planRequired="Higher Plan"
          currentPlan={usageRaw?.currentPlan}
          usageLimit={usageRaw?.profilesLimit}
        />
      </MDBox>
    );
  }
);

export default SelectPrimaryProfile;
